import {
  AddCircleOutlined,
  RemoveCircleOutlined,
} from '@mui/icons-material';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormGroup,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { PickerDay } from '@mui/x-date-pickers/PickerDay';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';

import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';

import { getPricing } from '../../api/branchesAPI';
import {
  createService,
  getService,
  getServiceCapacity,
  getServicesByDate,
  getStatusChoices,
  updateService,
} from '../../api/servicesAPI';
import { getCurrentBranch } from '../../api/usersAPI';
import { getVehicle } from '../../api/vehiclesAPI';


// Creates an empty row object that dynamically assigns tax exemption
const createEmptyRow = (taxExemptionStatus) => ({
  id: null,
  description: '',
  quantity: '',
  part_price: '',
  labor_price: '',
  tax_exempt: taxExemptionStatus,
});

// TODO: Allow branch to set datetime default
const defaultServiceForm = {
  vehicle_id: null,
  branch_id: null,
  estimate_id: null,
  datetime: dayjs().hour(11).minute(0).second(0),
  estimated_time: '1 hr',
  arrival_type: '',
  service_items: [createEmptyRow(false)],
  customer_notes: '',
  internal_notes: '',
  mileage: '',
  status: 'Scheduled',
};

// TODO: Allow branch to set limits which will affect the schedule preview coloring
const HOURS_HARD_LIMIT = 12;
const COUNT_HARD_LIMIT = 6;

const LOAD_COLORS = {
  high: {
    bg: '#ffccbc',
    hover: '#ffab91',
    border: '#f44336',
    dot: '#d32f2f',
  },
  medium: {
    bg: '#ffe082',
    hover: '#ffd54f',
    border: 'transparent',
    dot: '#f9a825',
  },
  low: {
    bg: '#c8e6c9',
    hover: '#a5d6a7',
    border: 'transparent',
    dot: '#2e7d32',
  },
  none: {
    bg: 'transparent',
    hover: undefined,
    border: 'transparent',
    dot: 'transparent',
  },
};

const COMPLETED_STATUS = 'Completed';

function getLoadLevel(load) {
  const hours = load?.hours ?? 0;
  const count = load?.count ?? 0;

  const ratio = Math.max(
    HOURS_HARD_LIMIT ? hours / HOURS_HARD_LIMIT : 0,
    COUNT_HARD_LIMIT ? count / COUNT_HARD_LIMIT : 0
  );

  if (ratio >= 1) return 'high';
  if (ratio >= 0.75) return 'medium';
  if (ratio > 0) return 'low';

  return 'none';
}

function isCompletedStatus(status) {
  return String(status).trim().toLowerCase() === COMPLETED_STATUS.toLowerCase();
}

function formatArrivalType(value) {
  switch (value) {
    case 'drop_off':
      return 'Drop off';
    case 'stay':
      return 'Stay';
    default:
      return 'Unspecified';
  }
}

/** Custom day component for StaticDatePicker (MUI X v6+ slots API). */
function CapacityDay(props) {
  // Pull out custom prop so it doesn't get forwarded to the DOM element
  const { capacityByDate = {}, ...PickerDayProps } = props;

  const dateKey = props.day?.format('YYYY-MM-DD');
  const level = getLoadLevel(capacityByDate[dateKey]);
  const colors = LOAD_COLORS[level];

  return (
    <PickerDay
      {...PickerDayProps}
      sx={{
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        position: 'relative',
        '&:hover, &:focus': {
          backgroundColor: colors.hover || colors.bg,
        },
        '&.Mui-selected': {
          backgroundColor: colors.hover || 'primary.main',
          color: level !== 'none' ? '#000' : undefined,
        },
        ...(level !== 'none' && {
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 4,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 10,
            height: 3,
            borderRadius: 999,
            backgroundColor: colors.dot,
          },
        }),
      }}
    />
  );
}

const allowedKeys = [
  'vehicle_id',
  'branch_id',
  'datetime',
  'estimated_time',
  'arrival_type',
  'service_items',
  'customer_notes',
  'internal_notes',
  'mileage',
  'status',
  'estimate_id',
  'service_owner_id',
];

const nestedAllowedKeys = [
  'id',
  'description',
  'quantity',
  'part_price',
  'labor_price',
  'tax_exempt',
];

/**
 * A modal form for scheduling auto service work.
 *
 * @param {boolean} isOpen - Whether the modal is open or closed
 * @param {function} onClose - Function to close the modal
 * @param {number} vehicleId - ID of the vehicle associated with the service
 * @param {object} estimate - Estimate data to use in scheduling (optional)
 * @param {number} serviceId - ID of the service to edit (optional)
 * @param {Array<Object>} owners - Owners of this vehicle
 * @param {function} refreshFunction - Function to refresh the parent component (optional)
 */
function ServiceModal({
  isOpen,
  onClose,
  vehicleId,
  estimate = null,
  serviceId = null,
  owners = [],
  refreshFunction = null,
}) {
  const [currentBranch, setCurrentBranch] = useState(null);
  const [currentBranchName, setCurrentBranchName] = useState(null);

  // Fetched pricing options for the current branch or use business pricing if not available
  const [defaultPricing, setDefaultPricing] = useState([]);

  // Default tax exemption based on the selected service owner
  const [defaultTaxExemption, setDefaultTaxExemption] = useState(false);

  // Object of the user that the service was made for
  const [serviceOwner, setServiceOwner] = useState(null);
  const [availableOwners, setAvailableOwners] = useState([]);
  const [loadedServiceStatus, setLoadedServiceStatus] = useState('');

  const [statusChoices, setStatusChoices] = useState([]);

  const [serviceForm, setServiceForm] = useState({
    ...defaultServiceForm,
    vehicle_id: vehicleId,
  });

  // Calendar date that user wants to preview to gauge business
  const [previewDate, setPreviewDate] = useState(dayjs());

  // Scheduled services based on preview date
  const [scheduledServices, setScheduledServices] = useState([]);

  // Used for capacity color coding
  const [calendarMonth, setCalendarMonth] = useState(dayjs());
  const [serviceCapacityByDate, setServiceCapacityByDate] = useState({});

  // Form submission errors
  const [errors, setErrors] = useState({});

  const getDefaultReopenedStatus = useCallback((choices) => {
    return (
      (choices ?? statusChoices).find(
        (statusOption) => !isCompletedStatus(statusOption[0])
      )?.[0] ?? ''
    );
  }, [statusChoices]);

  /**
   * Resets variables for the next use and tells the parent component
   * to close the modal.
   *
   * @param {object} event
   * @param {string} reason
   */
  const handleClose = (event, reason) => {
    // Prevent escaping modal with backdrop click
    if (reason === 'backdropClick') {
      return;
    }

    // Reset data for the next time the modal opens
    setServiceOwner(null);
    setAvailableOwners([]);
    setDefaultTaxExemption(false);

    setServiceForm({
      ...defaultServiceForm,
      vehicle_id: null,
      service_items: [createEmptyRow(false)],
    });

    setCalendarMonth(dayjs());
    setServiceCapacityByDate({});
    setPreviewDate(dayjs());
    setScheduledServices([]);
    setErrors({});

    onClose();
  };

  /**
   * Changes the customer the service was created for.
   * Impacts tax exemption and is used on service documents.
   *
   * @param {object|null} owner - Selected vehicle owner
   */
  const handleOwnerChange = useCallback((owner) => {
    // If owner gets deselected, set service owner to null but keep current tax exemptions
    if (owner === null) {
      setServiceOwner(null);
      return;
    }

    setServiceForm((currentForm) => ({
      ...currentForm,
      service_owner_id: owner.id,
      service_items: currentForm.service_items.map((row) => ({
        ...row,
        tax_exempt: owner.tax_exempt,
      })),
    }));

    setDefaultTaxExemption(owner.tax_exempt);
    setServiceOwner(owner);
  }, []);

  /** Update a specific field in the service form. */
  const handleFieldChange = useCallback((field, value) => {
    setServiceForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));

    if (field === 'datetime' && value && dayjs(value).isValid()) {
      const nextDate = dayjs(value);
      setPreviewDate(nextDate);
      setCalendarMonth(nextDate);
    }
  }, []);

  /** Adds a blank row to service_items. */
  const handleAddRow = () => {
    setServiceForm((currentForm) => ({
      ...currentForm,
      service_items: [
        ...currentForm.service_items,
        createEmptyRow(defaultTaxExemption),
      ],
    }));
  };

  /** Update multiple fields within a service item row. */
  const updateRow = useCallback((index, updates) => {
    setServiceForm((currentForm) => ({
      ...currentForm,
      service_items: currentForm.service_items.map((row, rowIndex) =>
        rowIndex === index
          ? { ...row, ...updates }
          : row
      ),
    }));
  }, []);

  /** Update a specific field within a service item row. */
  const updateRowField = useCallback((index, field, value) => {
    setServiceForm((currentForm) => ({
      ...currentForm,
      service_items: currentForm.service_items.map((row, rowIndex) =>
        rowIndex === index
          ? { ...row, [field]: value }
          : row
      ),
    }));
  }, []);

  /** Removes a service item row. */
  const handleRemoveRow = (index) => {
    setServiceForm((currentForm) => {
      // Ensure at least one row exists
      if (currentForm.service_items.length === 1) {
        return currentForm;
      }

      return {
        ...currentForm,
        service_items: currentForm.service_items.filter(
          (row, rowIndex) => rowIndex !== index
        ),
      };
    });
  };

  /** Searches for services on a particular date. */
  const handleSchedulePreview = async (value) => {
    const selectedDate = dayjs(value);

    if (!value || !selectedDate.isValid()) {
      return;
    }

    // Set date for schedule preview widget
    setPreviewDate(selectedDate);
    setCalendarMonth(selectedDate);

    // Preserve the time currently selected when changing the date from the preview
    const newDate = selectedDate;
    const currentDateTime = dayjs(serviceForm.datetime || dayjs());

    const combinedDateTime = newDate
      .hour(currentDateTime.hour())
      .minute(currentDateTime.minute())
      .second(currentDateTime.second())
      .millisecond(currentDateTime.millisecond());

    // Update datetime with the new date while preserving the time
    handleFieldChange('datetime', combinedDateTime);

    // Format date for the API endpoint
    const dateStr = combinedDateTime.format('YYYY-M-D');

    // Make a GET request to get scheduled services for a particular date
    try {
      const response = await getServicesByDate(dateStr);
      setScheduledServices(response.data);
    } catch (error) {
      console.error('Error fetching scheduled services:', error);
    }
  };

  /** Fetch monthly service capacity used for calendar color coding. */
  const fetchCapacityForMonth = useCallback(async (monthValue, branchId) => {
    if (!branchId || !monthValue) {
      return;
    }

    try {
      const monthStart = monthValue.startOf('month');
      const monthEnd = monthValue.endOf('month');

      const response = await getServiceCapacity(
        monthStart.format('YYYY-MM-DD'),
        monthEnd.format('YYYY-MM-DD'),
        branchId
      );

      setServiceCapacityByDate(response.data?.days ?? {});
    } catch (error) {
      console.error('Error fetching service capacity:', error);
    }
  }, []);

  /**
   * Filters an object so only API-approved keys are submitted.
   *
   * @param {object} data
   * @param {Array<string>} allowedObjectKeys
   * @param {string|null} nestedKey
   * @param {Array<string>|null} allowedNestedKeys
   * @returns {object}
   */
  function filterObject(
    data,
    allowedObjectKeys,
    nestedKey = null,
    allowedNestedKeys = null
  ) {
    // Filter top-level keys
    const filteredData = Object.keys(data).reduce((accumulator, key) => {
      if (allowedObjectKeys.includes(key)) {
        accumulator[key] = data[key];
      }

      return accumulator;
    }, {});

    // If there is a nested key to filter
    if (nestedKey && nestedKey in data && allowedNestedKeys) {
      filteredData[nestedKey] = data[nestedKey].map((item) =>
        Object.keys(item).reduce((accumulator, key) => {
          if (allowedNestedKeys.includes(key)) {
            accumulator[key] = item[key];
          }

          return accumulator;
        }, {})
      );
    }

    return filteredData;
  }

  /** Submit form for either creating or editing a service. */
  const handleSubmit = async () => {
    const selectedServiceOwnerId =
      serviceOwner?.id ??
      serviceForm.service_owner_id ??
      null;

    if (selectedServiceOwnerId === null) {
      setErrors({
        error: 'No service owner set',
      });
      return;
    }

    try {
      // Filter serviceForm to only include values accepted by the API
      const filteredServiceData = filterObject(
        serviceForm,
        allowedKeys,
        'service_items',
        nestedAllowedKeys
      );

      // Ensure required foreign keys are present
      filteredServiceData.branch_id =
        filteredServiceData.branch_id ?? currentBranch;

      filteredServiceData.vehicle_id =
        filteredServiceData.vehicle_id ?? vehicleId;

      filteredServiceData.service_owner_id =
        selectedServiceOwnerId;

      // Remove blank service-item rows before submitting
      filteredServiceData.service_items =
        filteredServiceData.service_items.filter(
          (item) => item.description
        );

      if (serviceId && isCompletedStatus(loadedServiceStatus)) {
        filteredServiceData.customer_signature_image = '';
        filteredServiceData.customer_signature_sig_string = '';
        filteredServiceData.customer_signed_at = null;
      }

      // Send null instead of an empty string for mileage
      if (
        filteredServiceData.mileage === '' ||
        filteredServiceData.mileage === undefined
      ) {
        filteredServiceData.mileage = null;
      } else {
        const numericMileage = Number(filteredServiceData.mileage);

        filteredServiceData.mileage = Number.isNaN(numericMileage)
          ? null
          : numericMileage;
      }

      if (!serviceId) {
        await createService(filteredServiceData);
      } else {
        const updatedServiceData = {
          ...filteredServiceData,
          branch:
            filteredServiceData.branch_id ??
            currentBranch,
          vehicle:
            filteredServiceData.vehicle_id ??
            vehicleId,
          service_owner:
            selectedServiceOwnerId,
        };

        delete updatedServiceData.branch_id;
        delete updatedServiceData.vehicle_id;
        delete updatedServiceData.service_owner_id;

        await updateService(
          serviceId,
          updatedServiceData
        );
      }

      // Refresh the parent component if a refresh function was provided
      if (refreshFunction) {
        refreshFunction();
      }

      handleClose();
    } catch (error) {
      if (error.response) {
        setErrors(error.response.data);
        console.error('Response error:', error.response.data);
      } else if (error.request) {
        setErrors({
          error: 'The server did not respond.',
        });
        console.error('Request error:', error.request);
      } else {
        setErrors({
          error: error.message,
        });
        console.error('Error:', error.message);
      }
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let isMounted = true;

    async function initializeModal() {
      try {
        setErrors({});
        setScheduledServices([]);

        const [
          branchResponse,
          pricingResponse,
          statusResponse,
          serviceResponse,
        ] = await Promise.all([
          getCurrentBranch(),
          getPricing(),
          getStatusChoices(),
          serviceId
            ? getService(serviceId)
            : Promise.resolve(null),
        ]);

        if (!isMounted) {
          return;
        }

        const branchId = branchResponse.data.current_branch;
        const service = serviceResponse?.data ?? null;
        const resolvedVehicleId =
          service?.vehicle?.id ??
          service?.vehicle_id ??
          vehicleId ??
          null;
        const vehicleResponse =
          owners.length === 0 &&
          resolvedVehicleId
            ? await getVehicle(resolvedVehicleId)
            : null;
        const ownerOptions =
          owners.length > 0
            ? owners
            : (
                vehicleResponse?.data
                  ?.list_owners_object ?? []
              );
        const serviceOwnerId =
          service?.service_owner_details?.id ??
          service?.service_owner?.id ??
          service?.service_owner_id ??
          (typeof service?.service_owner === 'number'
            ? service.service_owner
            : null) ??
          estimate?.estimate_owner_details?.id ??
          null;

        const initialOwner =
          ownerOptions.find(
            (owner) => owner.id === serviceOwnerId
          ) ??
          service?.service_owner_details ??
          (
            service?.service_owner &&
            typeof service.service_owner === 'object'
              ? service.service_owner
              : null
          ) ??
          estimate?.estimate_owner_details ??
          (ownerOptions.length === 1
            ? ownerOptions[0]
            : null);

        const initialTaxExemption =
          initialOwner?.tax_exempt ?? false;

        let initialItems;

        if (service) {
          initialItems = service.service_items?.length
            ? service.service_items.map((item) => ({
                ...item,
              }))
            : [createEmptyRow(initialTaxExemption)];
        } else if (estimate?.estimate_items?.length) {
          // Estimate item IDs do not belong to the new service items
          initialItems = estimate.estimate_items.map((item) => ({
            ...item,
            id: null,
            tax_exempt: initialTaxExemption,
          }));
        } else {
          initialItems = [
            createEmptyRow(initialTaxExemption),
          ];
        }

        const initialForm = service
          ? {
              ...defaultServiceForm,
              ...service,
              vehicle_id:
                service.vehicle?.id ??
                service.vehicle_id ??
                vehicleId ??
                null,
              branch_id:
                service.branch_details?.id ??
                service.branch?.id ??
                service.branch_id ??
                (typeof service.branch === 'number'
                  ? service.branch
                  : null) ??
                branchId,
              service_owner_id:
                serviceOwnerId ??
                initialOwner?.id ??
                null,
              arrival_type: service.arrival_type ?? '',
              service_items: initialItems,
              mileage: service.mileage ?? '',
            }
          : {
              ...defaultServiceForm,
              vehicle_id: vehicleId ?? null,
              branch_id: branchId,
              estimate_id: estimate?.id ?? null,
              service_owner_id:
                initialOwner?.id ?? null,
              arrival_type: '',
              service_items: initialItems,
            };

        setCurrentBranch(branchId);
        setCurrentBranchName(
          branchResponse.data.current_branch_name
        );

        setDefaultPricing(
          pricingResponse.data.default_pricing ?? []
        );

        setAvailableOwners(ownerOptions);
        const filteredStatusChoices = (statusResponse.data ?? []).filter(
          (statusOption) => !isCompletedStatus(statusOption[0])
        );

        setStatusChoices(filteredStatusChoices);
        setServiceOwner(initialOwner);
        setDefaultTaxExemption(initialTaxExemption);
        setLoadedServiceStatus(service?.status ?? '');
        setServiceForm({
          ...initialForm,
          status:
            service &&
            isCompletedStatus(service.status)
              ? getDefaultReopenedStatus(filteredStatusChoices)
              : initialForm.status,
        });

        const initialDate = dayjs(initialForm.datetime);

        if (initialDate.isValid()) {
          setPreviewDate(initialDate);
          setCalendarMonth(initialDate);
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setErrors({
          error: 'Unable to load the service form.',
        });

        console.error(
          'Error initializing service modal:',
          error
        );
      }
    }

    initializeModal();

    return () => {
      isMounted = false;
    };
  }, [
    isOpen,
    serviceId,
    estimate,
    owners,
    vehicleId,
  ]);

  useEffect(() => {
    if (!isOpen || !currentBranch) {
      return;
    }

    fetchCapacityForMonth(
      calendarMonth,
      currentBranch
    );
  }, [
    isOpen,
    currentBranch,
    calendarMonth,
    fetchCapacityForMonth,
  ]);

  // Define modal style
  const style = {
    position: 'absolute',
    top: '3vh',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'min(1500px, 96vw)',
    maxHeight: '94vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };

  const notesStyles = {
    mb: 1,
  };

  const getScheduledServiceOwnerName = (service) =>
    service?.service_owner_details?.name ??
    service?.service_owner?.name ??
    service?.customer ??
    'Unknown customer';

  const getScheduledServiceCustomerLabel = (service) =>
    getScheduledServiceOwnerName(service);

  const getScheduledServiceEstimatedTimeLabel = (service) => {
    const arrivalType = formatArrivalType(service.arrival_type);

    if (arrivalType === 'Unspecified') {
      return service.estimated_time;
    }

    return `${service.estimated_time} (${arrivalType})`;
  };

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby='service-modal-title'
        aria-describedby='service-modal-description'
      >
        <Box sx={style}>
          <Typography
            id='service-modal-title'
            variant='h5'
            component='h2'
          >
            {estimate
              ? 'Schedule Service From Estimate'
              : serviceId
                ? 'Edit Service'
                : 'Schedule Service'}
          </Typography>
          {serviceId && isCompletedStatus(loadedServiceStatus) && (
            <Alert severity='warning' sx={{ mb: 2 }}>
              Editing this completed invoice will delete the existing signature.
            </Alert>
          )}

          {errors.non_field_errors && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {errors.non_field_errors[0]}
            </Alert>
          )}

          {errors.error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {errors.error}
            </Alert>
          )}

          {currentBranch && (
            <Typography sx={{ mb: 1 }}>
              {currentBranchName
                ? `(${currentBranchName})`
                : ''}
            </Typography>
          )}

          <FormGroup
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '20px',
            }}
          >
            {/* Service Details Section */}
            <Box sx={{ flex: '4' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px',
                }}
              >
                <Typography
                  variant='h6'
                  sx={{ flex: '0 0 100%' }}
                >
                  Service Details
                </Typography>

                <Autocomplete
                  fullWidth
                  slotProps={{
                    listbox: {
                      style: {
                        maxHeight: 250,
                        overflow: 'auto',
                      },
                    },
                  }}
                  size='small'
                  id='owner-autocomplete'
                  value={serviceOwner}
                  options={availableOwners}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  getOptionLabel={(option) => {
                    if (!option) {
                      return '';
                    }

                    if (typeof option === 'string') {
                      return option;
                    }

                    if (option.tax_exempt) {
                      return `${option.name} (Tax Exempt)`;
                    }

                    return option.name;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Prepared For'
                    />
                  )}
                  onChange={(event, newOwner) =>
                    handleOwnerChange(newOwner)
                  }
                />

                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                >
                  <DateTimePicker
                    sx={{ flex: 1 }}
                    timeSteps={{ minutes: 15 }}
                    slotProps={{
                      textField: {
                        size: 'small',
                      },
                    }}
                    label='Service Date & Time'
                    value={
                      serviceForm.datetime
                        ? dayjs(serviceForm.datetime)
                        : dayjs()
                    }
                    onChange={(value) =>
                      handleFieldChange(
                        'datetime',
                        value
                      )
                    }
                  />
                </LocalizationProvider>

                <FormControl
                  sx={{ flex: 1 }}
                  size='small'
                >
                  <InputLabel id='estimated-time-select-label'>
                    Estimated Time
                  </InputLabel>

                  <Select
                    labelId='estimated-time-select-label'
                    id='estimated-time-select'
                    value={serviceForm.estimated_time}
                    label='Estimated Time'
                    onChange={(event) =>
                      handleFieldChange(
                        'estimated_time',
                        event.target.value
                      )
                    }
                  >
                    <MenuItem value='1 hr'>
                      1 hr
                    </MenuItem>
                    <MenuItem value='2 hrs'>
                      2 hrs
                    </MenuItem>
                    <MenuItem value='3 hrs'>
                      3 hrs
                    </MenuItem>
                    <MenuItem value='4 hrs'>
                      4 hrs
                    </MenuItem>
                    <MenuItem value='5 hrs'>
                      5 hrs
                    </MenuItem>
                    <MenuItem value='6 hrs'>
                      6 hrs
                    </MenuItem>
                    <MenuItem value='7 hrs'>
                      7 hrs
                    </MenuItem>
                    <MenuItem value='8 hrs'>
                      8 hrs
                    </MenuItem>
                    <MenuItem value='1+ day'>
                      1+ day
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  sx={{ flex: 1 }}
                  size='small'
                >
                  <InputLabel id='arrival-type-select-label'>
                    Drop Off / Stay
                  </InputLabel>

                  <Select
                    labelId='arrival-type-select-label'
                    id='arrival-type-select'
                    value={serviceForm.arrival_type ?? ''}
                    label='Drop Off / Stay'
                    onChange={(event) =>
                      handleFieldChange(
                        'arrival_type',
                        event.target.value
                      )
                    }
                  >
                    <MenuItem value=''>
                      Unspecified
                    </MenuItem>
                    <MenuItem value='drop_off'>
                      Drop off
                    </MenuItem>
                    <MenuItem value='stay'>
                      Stay
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Typography variant='h6'>
                Services
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '10px',
                }}
              >
                <Typography
                  display='inline'
                  sx={{ flexGrow: 1 }}
                >
                  Description
                </Typography>

                <Typography
                  display='inline'
                  sx={{ width: '60px' }}
                >
                  Qty
                </Typography>

                <Typography
                  display='inline'
                  sx={{ width: '125px' }}
                >
                  Part Price
                </Typography>

                <Typography
                  display='inline'
                  sx={{ width: '125px' }}
                >
                  Labor Price
                </Typography>

                <Typography
                  display='inline'
                  sx={{ width: '100px' }}
                >
                  Tax Exempt
                </Typography>

                <Typography
                  display='inline'
                  sx={{ width: '40px' }}
                />
              </Box>

              {serviceForm.service_items.map(
                (row, index) => (
                  <Box
                    key={row.id ?? index}
                    data-id={row.id}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '10px',
                      marginY: '10px',
                    }}
                  >
                    <Autocomplete
                      disablePortal
                      freeSolo
                      size='small'
                      sx={{ flexGrow: 1 }}
                      options={defaultPricing}
                      getOptionLabel={(option) =>
                        typeof option === 'string'
                          ? option
                          : option?.name ?? ''
                      }
                      inputValue={
                        row.description ?? ''
                      }
                      onInputChange={(
                        event,
                        newInputValue
                      ) => {
                        updateRowField(
                          index,
                          'description',
                          newInputValue
                        );
                      }}
                      renderInput={(params) => (
                        <TextField {...params} />
                      )}
                      onChange={(
                        event,
                        newValue
                      ) => {
                        if (
                          !newValue ||
                          typeof newValue === 'string'
                        ) {
                          return;
                        }

                        updateRow(index, {
                          description:
                            newValue.name ?? '',
                          quantity:
                            newValue.quantity ?? '',
                          part_price:
                            newValue.part_price ?? '',
                          labor_price:
                            newValue.labor_price ?? '',
                          tax_exempt:
                            defaultTaxExemption,
                        });
                      }}
                    />

                    <TextField
                      sx={{ width: '60px' }}
                      value={row.quantity ?? ''}
                      size='small'
                      onChange={(event) =>
                        updateRowField(
                          index,
                          'quantity',
                          event.target.value
                        )
                      }
                    />

                    <TextField
                      sx={{ width: '125px' }}
                      value={row.part_price ?? ''}
                      size='small'
                      onChange={(event) =>
                        updateRowField(
                          index,
                          'part_price',
                          event.target.value
                        )
                      }
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position='start'>
                              $
                            </InputAdornment>
                          ),
                        },
                      }}
                    />

                    <TextField
                      sx={{ width: '125px' }}
                      value={row.labor_price ?? ''}
                      size='small'
                      onChange={(event) =>
                        updateRowField(
                          index,
                          'labor_price',
                          event.target.value
                        )
                      }
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position='start'>
                              $
                            </InputAdornment>
                          ),
                        },
                      }}
                    />

                    <Checkbox
                      sx={{ width: '100px' }}
                      checked={Boolean(
                        row.tax_exempt
                      )}
                      onChange={(event) =>
                        updateRowField(
                          index,
                          'tax_exempt',
                          event.target.checked
                        )
                      }
                    />

                    <IconButton
                      onClick={() =>
                        handleRemoveRow(index)
                      }
                      disabled={
                        serviceForm.service_items
                          .length === 1
                      }
                    >
                      <RemoveCircleOutlined />
                    </IconButton>
                  </Box>
                )
              )}

              <Button
                onClick={handleAddRow}
                startIcon={<AddCircleOutlined />}
              >
                Add Item
              </Button>

              <TextField
                id='customer-notes'
                label='Customer Notes'
                variant='outlined'
                size='small'
                multiline
                rows={2}
                fullWidth
                value={
                  serviceForm.customer_notes ?? ''
                }
                onChange={(event) =>
                  handleFieldChange(
                    'customer_notes',
                    event.target.value
                  )
                }
                sx={notesStyles}
              />

              {/* New fields */}
              <TextField
                id='internal-notes'
                label='Internal Notes'
                variant='outlined'
                size='small'
                multiline
                rows={2}
                fullWidth
                value={
                  serviceForm.internal_notes ?? ''
                }
                onChange={(event) =>
                  handleFieldChange(
                    'internal_notes',
                    event.target.value
                  )
                }
                sx={notesStyles}
              />

              <Box
                sx={{
                  display: 'flex',
                  gap: '5px',
                }}
              >
                <TextField
                  id='mileage'
                  label='Mileage'
                  variant='outlined'
                  size='small'
                  fullWidth
                  value={serviceForm.mileage ?? ''}
                  onChange={(event) =>
                    handleFieldChange(
                      'mileage',
                      event.target.value
                    )
                  }
                  sx={{ flex: 1 }}
                />

                <FormControl
                  sx={{ flex: 1 }}
                  size='small'
                >
                  <InputLabel id='status-select-label'>
                    Status
                  </InputLabel>

                  <Select
                    labelId='status-select-label'
                    id='status-select'
                    value={serviceForm.status ?? ''}
                    label='Status'
                    onChange={(event) =>
                      handleFieldChange(
                        'status',
                        event.target.value
                      )
                    }
                  >
                    {statusChoices.map(
                      (statusOption) => (
                        <MenuItem
                          key={statusOption[0]}
                          value={statusOption[0]}
                        >
                          {statusOption[1]}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Box>

              <br />

              <Box
                sx={{
                  display: 'flex',
                  gap: '15px',
                }}
              >
                <Button
                  variant='outlined'
                  onClick={handleSubmit}
                >
                  Submit
                </Button>

                <Button
                  variant='outlined'
                  color='error'
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
            {/* End of service details section */}

            {/* Schedule Preview Section */}
            <Box sx={{ flex: '4' }}>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 720,
                  mx: 'auto',
                }}
              >
                <Typography
                  align='center'
                  variant='h6'
                  sx={{ marginBottom: 1.5 }}
                >
                  Preview Schedule
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        backgroundColor: '#c8e6c9',
                        border: '1px solid #2e7d32',
                        borderRadius: '2px',
                      }}
                    />

                    <Typography variant='caption'>
                      Light load
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        backgroundColor: '#ffe082',
                        border: '1px solid #f9a825',
                        borderRadius: '2px',
                      }}
                    />

                    <Typography variant='caption'>
                      Filling up
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        backgroundColor: '#ffccbc',
                        border: '1px solid #f44336',
                        borderRadius: '2px',
                      }}
                    />

                    <Typography variant='caption'>
                      At / over capacity
                    </Typography>
                  </Box>
                </Box>

                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                >
                  <StaticDatePicker
                    value={previewDate}
                    onChange={handleSchedulePreview}
                    onMonthChange={(month) => {
                      setCalendarMonth(month);
                    }}
                    onYearChange={(year) => {
                      setCalendarMonth(year);
                    }}
                    referenceDate={calendarMonth}
                    slots={{
                      day: CapacityDay,
                    }}
                    slotProps={{
                      day: {
                        capacityByDate:
                          serviceCapacityByDate,
                      },
                      toolbar: {
                        hidden: true,
                      },
                      actionBar: {
                        actions: [],
                      },
                    }}
                    sx={{
                      mb: 0.5,
                      '& .MuiPickersCalendarHeader-root':
                        {
                          marginTop: 0,
                        },
                    }}
                  />
                </LocalizationProvider>

                <Box sx={{ mb: 1, textAlign: 'center' }}>
                  <Typography variant='h6'>
                    Scheduled Services
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                  >
                    {dayjs(previewDate).format('MMMM D, YYYY')}
                  </Typography>
                </Box>
              </Box>

              <TableContainer
                component={Paper}
                sx={{
                  textAlign: 'center',
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          padding: '8px 3px 8px 10px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Time
                      </TableCell>

                      <TableCell
                        sx={{ padding: '8px 3px', textAlign: 'left' }}
                      >
                        Customer
                      </TableCell>

                      <TableCell
                        sx={{ padding: '8px 3px', whiteSpace: 'nowrap' }}
                      >
                        Est. Time
                      </TableCell>

                      <TableCell
                        sx={{ padding: '8px 3px' }}
                      >
                        Description
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {scheduledServices.length > 0 ? (
                      scheduledServices.map(
                        (service) => (
                          <TableRow key={service.id}>
                            <TableCell
                              sx={{
                                padding: '8px 3px 8px 10px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {new Date(
                                service.datetime
                              ).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </TableCell>

                            <TableCell
                              sx={{
                                padding: '8px 3px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {getScheduledServiceCustomerLabel(service)}
                            </TableCell>

                            <TableCell
                              sx={{
                                padding: '8px 3px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {getScheduledServiceEstimatedTimeLabel(service)}
                            </TableCell>

                            <TableCell
                              sx={{
                                padding: '8px 3px',
                              }}
                            >
                              {
                                service.service_items_str
                              }
                            </TableCell>
                          </TableRow>
                        )
                      )
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{ textAlign: 'center' }}
                        >
                          No Services Scheduled for{' '}
                          {dayjs(previewDate).toDate().toDateString()}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            {/* End of schedule preview section */}
          </FormGroup>
        </Box>
      </Modal>
    </>
  );
}

export default ServiceModal;